import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { HideCandidatesActionTypes } from "@/components/candidate-dialog/actions/hide-action/HideAction";
import { IDisqualificationInitiator, ManagerEvaluation } from "@/components/candidate-dialog/shared";
import { Candidate, ICandidate } from "@/services/DTOs/Candidate";
import { CandidateBio } from "@/services/DTOs/CandidateBio";
import { CandidateContext } from "@/services/DTOs/CandidateContext";
import { CandidateDisqualifiedSummary } from "@/services/DTOs/CandidateDisqualifiedSummary";
import { CandidateRanking } from "@/services/DTOs/CandidateRanking";
import { CandidateRankSummary } from "@/services/DTOs/CandidateRankSummary";
import { CandidateResponse, ICandidateResponse } from "@/services/DTOs/CandidateResponse";
import { CandidateResponseSimilar, ICandidateResponseSimilar } from "@/services/DTOs/CandidateResponseSimilar";
import { CandidateStatus } from "@/services/DTOs/CandidateStatus";
import { CandidateState, CandidateSummary, ICandidateSummary } from "@/services/DTOs/CandidateSummary";
import { NearbyCandidates } from "@/services/DTOs/NearbyCandidates";
import { OpportunityApplicationFromGoogle } from "@/services/DTOs/OpportunityApplicationFromGoogle";
import { OpportunityCount } from "@/services/DTOs/OpportunityCount";
import { IPageable } from "@/services/DTOs/Pageable";
import { ISaveCandidateResponseFilterResult, SaveCandidateResponse } from "@/services/DTOs/SaveCandidateResponse";
import { SavePerson } from "@/services/DTOs/SavePerson";
import { IPageableSearch } from "@/services/DTOs/search/PageableSearch";
import { IPeopleEvalResult } from "@/services/DTOs/search/SearchResult";
import { trackAPICall } from "@/services/SnowplowService";
import { getId } from "@/utils/OpportunityUtils";
import { OpportunitiesApiService } from "./OpportunitiesApiService";

export interface IUpdateManagerEvaluation {
  interest: ManagerEvaluation;
  restoreColumn?: boolean;
  restoreStatus?: string;
  sendNotification?: boolean;
  trackingCode?: string;
  reasons?: IUpdateManagerEvaluationReasons[];
  disqualificationInitiator?: IDisqualificationInitiator;
  source?: string;
}

export interface IUpdateManagerEvaluationReasons {
  reason?: string;
  code?: string;
  level?: string;
  value?: string;
  context?: string;
  feedback?: string;
}

class Service {
  public getCandidatesByStateOrId(
    opportunityId: string,
    stateOrId?: string,
    page: number = 0,
    pageSize?: number,
    query?: string,
    cancelable: boolean = false,
    candidateStatus?: string[],
    isMatched?: boolean,
    isHidden?: boolean,
    matchStatus?: string[]
  ): Observable<IPageable<CandidateSummary>> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-state`;
    const params = new URLSearchParams();
    const headers = { "Cache-Control": "no-cache" };

    params.append("page", page.toString());
    if (stateOrId) {
      params.append("state", stateOrId);
    }
    if (query) {
      params.append("query", query);
    }
    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }
    if (candidateStatus) {
      params.append("candidateStatus", candidateStatus.join(","));
    }
    if (isMatched !== undefined) {
      params.append("isMatched", isMatched.toString());
    }
    if (isHidden !== undefined) {
      params.append("isHidden", isHidden.toString());
    }
    if (matchStatus !== undefined) {
      params.append("matchStatus", matchStatus.join(","));
    }

    const request = cancelable ?
      OpportunitiesApiService.cancellableGet(url, params) :
      OpportunitiesApiService.get(url, { headers, params });

    return request.pipe(map(
      (response) =>
        ({
        ...response.data,
        elements: response.data.elements.map(
          (c: ICandidateSummary) => new CandidateSummary({ ...c })
        )
      }) as IPageable<CandidateSummary>
    ));
  }

  public countCandidatesByStateOrId(opportunityId: string, stateOrId?: string): Observable<number> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-state/_count`;
    const params = new URLSearchParams();
    if (stateOrId) {
      params.append("state", stateOrId);
    }

    const request = OpportunitiesApiService.get(url, { params });

    return request.pipe(map((response) => response.data.count as number));
  }

  public getCandidateSummary(opportunityId: string, ggId: string): Observable<CandidateSummary> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${ggId}`;

    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new CandidateSummary(response.data)
    ));
  }

  public getCandidatesForOpportunitiesByCurrentPerson(opportunitiesIds: string[]): Observable<Candidate[]> {
    const url = `/api/suite/opportunities/${opportunitiesIds.map((id) => getId(id)).join(",")}/candidates/me`;

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data.map((c: ICandidate) => new Candidate(c))));
  }

  public getRankedCandidatesSummaries(opportunityId: string, page: number = 0, pageSize?: number): Observable<IPageable<CandidateSummary>> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-rank/me`;
    const params = new URLSearchParams();
    params.append("page", page.toString());
    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }

    return OpportunitiesApiService.get(url, { params }).pipe(map(
      (response) => ({
        ...response.data,
        elements: response.data.elements.map((c: ICandidateSummary) => new CandidateSummary(c))
      }) as IPageable<CandidateSummary>
    ));
  }

  public getNearbyRankedCandidatesSummaries(opportunityId: string): Observable<NearbyCandidates> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-rank-near/me`;

    return OpportunitiesApiService.get(url).pipe(map((response) => new NearbyCandidates(response.data)));
  }

  public getReviewedCandidates(opportunityId: string): Observable<number> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/reviewed-candidates-count`;

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public getReviewedCandidatesByOpportunitiesIds(opportunitiesIds: string[]): Observable<OpportunityCount[]> {
    const url = `/api/suite/opportunities/${opportunitiesIds.map((id) => getId(id)).join(",")}/reviewed-candidates-counts`;

    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  @trackAPICall("POST", "Candidates", "candidate")
  public add(opportunityId: string, data: SavePerson): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates`;
    return OpportunitiesApiService.post(url, data).pipe(map((response) => response.data.id));
  }

  @trackAPICall("PUT", "Candidates", "candidateColumn")
  public updateCandidateColumn(opportunityId: string, candidateId: string,
                               data: { columnId: string, position: number }): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/column`;
    return OpportunitiesApiService.put(url, data).pipe(map(() => true));
  }

  @trackAPICall("PUT", "Candidates", "candidateInterest")
  public updateInterest(opportunityId: string, candidateId: string, data: { interest: boolean }): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/interest`;
    return OpportunitiesApiService.put(url, data).pipe(map(() => true));
  }

  @trackAPICall("PUT", "Candidates", "publicApplication")
  public updatePublicApplication(opportunityId: string, candidateId: string, data: { publicApplication: boolean }): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/public-application`;
    return OpportunitiesApiService.put(url, data).pipe(map(() => true));
  }

  @trackAPICall("PUT", "Candidates", "retractApplication")
  public retractApplication(opportunityId: string, candidateId: string): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/retract`;
    return OpportunitiesApiService.put(url, {}).pipe(map(() => true));
  }

  @trackAPICall("PUT", "Candidates", "review")
  public reviewCandidate(opportunityId: string, candidateId: string): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/review`;
    return OpportunitiesApiService.put(url, {}).pipe(map(() => true));
  }

  public getCandidateBio(opportunityId: string, subjectId: string): Observable<CandidateBio> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${subjectId}/bio`;

    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new CandidateBio(response.data)
    ));
  }

  public getCandidateBioByUsername(opportunityId: string, username: string): Observable<CandidateBio> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${username}/bio-by-username`;

    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new CandidateBio(response.data)
    ));
  }

  public getCandidateDetail(opportunityId: string, candidateId: string): Observable<Candidate> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/detail`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new Candidate(response.data)
    ));
  }

  public getCandidateStatus(opportunityId: string, ggId: string): Observable<CandidateStatus> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${ggId}/status`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => new CandidateStatus(response.data)
    ));
  }

  public getCandidateContext(opportunityId: string, subjectId: string): Observable<CandidateContext[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${subjectId}/context`;
    return OpportunitiesApiService.get(url).pipe(map(
      (response) => response.data
    ));
  }

  @trackAPICall("PUT", "Candidates", "managerFeedback")
  public updateManagerFeedback(opportunityId: string, candidateId: string, feedback: string): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/manager-feedback`;
    return OpportunitiesApiService.put(url, { feedback }).pipe(map(() => true));
  }

  @trackAPICall("PUT", "Candidates", "managerEvaluationReasons")
  public updateManagerEvaluationReasons(opportunityId: string, ggId: string, body: IUpdateManagerEvaluation): Observable<string> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-people/${ggId}/evaluationReasons`;
    return OpportunitiesApiService.put(url, body).pipe(map((response) => response.data.candidateId));
  }

  @trackAPICall("GET", "Candidates", "screeningQuestionsAnswers")
  public getCandidateAnswers(opportunityId: string, candidateId: string): Observable<CandidateResponse[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/responses`;
    return OpportunitiesApiService.get(url).pipe(map((response) =>
      response.data.map((answer: ICandidateResponse) => new CandidateResponse(answer))
    ));
  }

  @trackAPICall("POST", "Candidates", "screeningQuestionsAnswers")
  public addCandidateAnswer(
    opportunityId: string,
    candidateId: string,
    answer: SaveCandidateResponse
  ): Observable<ISaveCandidateResponseFilterResult> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/responses`;
    return OpportunitiesApiService.post(url, answer).pipe(map((response) => response.data));
  }

  @trackAPICall("PUT", "Candidates", "screeningQuestionsAnswers")
  public updateCandidateAnswer(
    opportunityId: string,
    candidateId: string,
    answer: SaveCandidateResponse
  ): Observable<ISaveCandidateResponseFilterResult> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/responses`;
    return OpportunitiesApiService.put(url, answer).pipe(map((response) => response.data));
  }

  public getCandidateSimilarAnswers(opportunityId: string): Observable<CandidateResponseSimilar[]> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/me/questions/similar`;
    return OpportunitiesApiService.get(url).pipe(map((response) =>
      response.data.map((answer: ICandidateResponseSimilar) => new CandidateResponseSimilar(answer))
    ));
  }

  @trackAPICall("PUT", "Candidates", "candidateApplicationStep")
  public saveApplicationStep(opportunityId: string,
                             candidateId: string,
                             currentStep: string,
                             currentQuestionId?: string): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/application-step`;
    const stepToSave = {
      step: currentStep,
      ... (currentQuestionId && {detail: currentQuestionId})
    };

    return OpportunitiesApiService.put(url, stepToSave).pipe(map(() => true));
  }

  public getOneClickLoginUrlToContinueApplication(opportunityId: string): Observable<{ url: string }> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/me/continue-url`;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public matchAndRank(
    opportunityId: string,
    state: CandidateState,
    ggIds: string[],
    userGgId?: string,
    applicantsColumn: boolean = false
  ): Observable<IPageableSearch<IPeopleEvalResult>> {
    if (!ggIds.length) {
      return of({ total: 0, offset: 0, size: 0, pagination: { previous: null, next: null }, results: [] });
    }

    const authHeaders = userGgId ? { "X-Torre-GgId": userGgId } : {};
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-ranking/${state}/${ggIds.join(",")}`;
    const params = new URLSearchParams();
    if (applicantsColumn) {
      params.append("context", "candidate");
    }

    return OpportunitiesApiService.get(url, { headers: authHeaders, params }).pipe(map((response) => {
      return response.data;
    }));
  }

  public getRankForOpportunityByCurrentPerson(opportunityId: string): Observable<CandidateRanking> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates-ranking/me`;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data.length ? response.data[0] : response.data));
  }

  public getRanksForOpportunitiesByCurrentPerson(
    opportunitiesIds: string[],
    applicantsOnly: boolean
  ): Observable<CandidateRankSummary[]> {
    const url = `/api/suite/opportunities/${opportunitiesIds.map((id) => getId(id)).join(",")}/candidates-ranking/me`;
    const params = new URLSearchParams();

    params.append("applicantsOnly", applicantsOnly.toString());

    return OpportunitiesApiService.get(url, { params }).pipe(map((response) => response.data));
  }

  public getDisqualifiedForOpportunitiesByCurrentPerson(opportunitiesIds: string[]): Observable<CandidateDisqualifiedSummary[]> {
    const url = `/api/suite/opportunities/${opportunitiesIds.map((id) => getId(id)).join(",")}/candidates-disqualified/me`;
    return OpportunitiesApiService.get(url).pipe(map((response) => response.data));
  }

  public getApplicationsFromGoogleForJobs(opportunitiesIds: string[], subjectId: string): Observable<OpportunityApplicationFromGoogle[]> {
    const url = `/api/suite/opportunities/${subjectId}/applied-from-google`;
    return OpportunitiesApiService.post(url, opportunitiesIds).pipe(map((response) => response.data));
  }

  public hideCandidate(opportunityId: string, candidateId: string, type: HideCandidatesActionTypes, hours?: number): Observable<boolean> {
    const url = `/api/suite/opportunities/${getId(opportunityId)}/candidates/${candidateId}/hidden`;
    return OpportunitiesApiService.put(url, { type, hours }).pipe(map(() => true));
  }
}

export const CandidatesService = new Service();
